import { redirect } from "next/navigation"
import type {
  AccountOrderDetail,
  OrderAddress,
  OrderTotalLine,
} from "@/api/account-order-detail"
import { Actions } from "@/components/checkout/thank-you/actions"
import { CustomerInfo } from "@/components/checkout/thank-you/customer-info"
import { OrderItems } from "@/components/checkout/thank-you/order-items"
import { OrderSummary } from "@/components/checkout/thank-you/order-summary"
import { SuccessHero } from "@/components/checkout/thank-you/success-hero"
import { getSession } from "@/services/auth-service"
import {
  getWordPressOrderDetail,
  type WordPressOrderAddress,
} from "@/services/wordpress-service"

interface ObrigadoPageProps {
  searchParams?: Promise<{
    orderId?: string
    key?: string
  }>
}

function formatCurrency(value: number, currency: string): string {
  const currencyCode = currency?.trim() ? currency.toUpperCase() : "BRL"
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currencyCode,
  }).format(value)
}

function transformAddress(wpAddress: WordPressOrderAddress): OrderAddress {
  return {
    firstName: wpAddress.first_name,
    lastName: wpAddress.last_name,
    company: wpAddress.company,
    address1: wpAddress.address_1,
    address2: wpAddress.address_2,
    city: wpAddress.city,
    state: wpAddress.state,
    postcode: wpAddress.postcode,
    country: wpAddress.country,
    phone: wpAddress.phone,
    email: wpAddress.email,
  }
}

export default async function ObrigadoPage({
  searchParams,
}: ObrigadoPageProps) {
  // Check authentication
  const session = await getSession()

  if (!session) {
    redirect("/login?redirect=/obrigado")
  }

  // Get and validate search params
  const resolvedSearchParams = await searchParams
  const orderId = resolvedSearchParams?.orderId?.trim()

  if (!orderId) {
    redirect("/")
  }

  // Fetch order data directly from WordPress
  const wpOrder = await getWordPressOrderDetail(
    session.wordpressSessionCookie,
    orderId
  )

  // Transform WordPress order to AccountOrderDetail format
  const totals: OrderTotalLine[] = [
    {
      label: "Subtotal",
      value: formatCurrency(Number(wpOrder.subtotal), wpOrder.currency),
    },
  ]

  if (Number(wpOrder.discount_total) > 0) {
    totals.push({
      label: "Desconto",
      value: formatCurrency(Number(wpOrder.discount_total), wpOrder.currency),
    })
  }

  if (Number(wpOrder.shipping_total) > 0) {
    totals.push({
      label: "Frete",
      value: formatCurrency(Number(wpOrder.shipping_total), wpOrder.currency),
    })
  }

  if (Number(wpOrder.total_tax) > 0) {
    totals.push({
      label: "Impostos",
      value: formatCurrency(Number(wpOrder.total_tax), wpOrder.currency),
    })
  }

  totals.push({
    label: "Total",
    value: formatCurrency(Number(wpOrder.total), wpOrder.currency),
  })

  const order: AccountOrderDetail = {
    id: Number(wpOrder.id),
    number: wpOrder.number,
    status: wpOrder.status,
    dateCreated: wpOrder.date_created,
    currency: wpOrder.currency,

    items: wpOrder.line_items.map((item) => ({
      id: item.id,
      name: item.name,
      quantity: Number(item.quantity),
      subtotal: Number(item.subtotal),
      total: Number(item.total),
      sku: item.sku,
      image: item.image?.src,
      variationAttributes: item.meta_data
        ?.filter((meta) => meta.display_key && meta.display_value)
        .map((meta) => ({
          name: meta.display_key ?? "",
          value: meta.display_value ?? "",
        })),
    })),

    totals,

    billingAddress: transformAddress(wpOrder.billing),
    shippingAddress: wpOrder.needs_shipping_address
      ? transformAddress(wpOrder.shipping)
      : undefined,
    needsShipping: wpOrder.needs_shipping_address,

    customerNote: wpOrder.customer_note,
    orderNotes:
      wpOrder.customer_order_notes?.map((note) => ({
        id: note.id,
        date: note.date_created,
        content: note.note,
      })) ?? [],
  }

  const totalLine = order.totals.find((t) => t.label === "Total")

  return (
    <main className='min-h-screen px-6 py-12'>
      <div className='mx-auto w-full max-w-4xl space-y-8'>
        <SuccessHero />

        <div className='grid gap-6 lg:grid-cols-2'>
          <OrderSummary
            dateCreated={order.dateCreated}
            orderNumber={order.number}
            status={order.status}
            total={totalLine?.value ?? "-"}
          />

          <div className='space-y-6'>
            <OrderItems
              currency={order.currency}
              items={order.items}
              totals={order.totals}
            />
          </div>
        </div>

        <CustomerInfo
          billingAddress={order.billingAddress}
          needsShipping={order.needsShipping}
          shippingAddress={order.shippingAddress}
        />

        <Actions orderNumber={order.number} />
      </div>
    </main>
  )
}
